import { Injectable } from '@angular/core';
import {
  Action,
  Selector,
  SelectorOptions,
  State,
  StateContext,
} from '@ngxs/store';
import {
  iif,
  insertItem,
  patch,
  removeItem,
  updateItem,
} from '@ngxs/store/operators';
import { SideBarControllerService } from '@osos/shared/api';
import {
  DataModuleWithViewItems,
  ManagedHttpContext,
  NavigationItem,
  NavigationMap,
  NavigationPresenterModel,
  NavigationPresenterModelV2,
  ServiceViewsStatusModel,
} from '@osos/shared/types';
import { switchMap, tap } from 'rxjs/operators';
import { NavigationFactoryV2Service } from '../../factories';
import { SocketService } from '../../services';
import { NavigationStateActions } from './navigation.actions';

export interface NavigationStateModel {
  navigationMap: NavigationMap;
  navigationItems: NavigationPresenterModel;
  navigationItemsV2: NavigationPresenterModelV2;
  servicesNavigationConfig: { enabled: boolean };
  sidebarStatus: ServiceViewsStatusModel;
}
@State<NavigationStateModel>({
  name: 'navigation',
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class NavigationState {
  constructor(
    private sideBarService: SideBarControllerService,
    private navFactoryV2Service: NavigationFactoryV2Service,
    private socketService: SocketService,
  ) {}

  @Selector()
  static currentNavigationMap(state: NavigationStateModel) {
    return state.navigationMap;
  }

  @Selector()
  static navigationItems(state: NavigationStateModel) {
    return state.navigationItems;
  }
  @Selector()
  static navigationItemsV2(state: NavigationStateModel) {
    return state.navigationItemsV2;
  }

  @Selector()
  static servicesNavigationConfig(state: NavigationStateModel) {
    return state.servicesNavigationConfig;
  }

  @Selector()
  static sidebarStatus(state: NavigationStateModel) {
    return state.sidebarStatus;
  }
  @Selector([NavigationState.sidebarStatus])
  static onGoingInboxCount(sidebarStatus: ServiceViewsStatusModel) {
    return sidebarStatus.onGoingInbox;
  }

  @Selector([NavigationState.sidebarStatus])
  static onGoingMyRequestsCount(sidebarStatus: ServiceViewsStatusModel) {
    return sidebarStatus.onGoingRequests;
  }

  @Action(NavigationStateActions.UpsertNavigationItem)
  upsertNavigationItem(
    { getState, setState }: StateContext<NavigationStateModel>,
    { module, navItem }: NavigationStateActions.UpsertNavigationItem,
  ) {
    const { navigationItems } = getState();

    const moduleItem = navigationItems.moduleViews.find(
      (mod) => mod.id === module?.id,
    );

    setState(
      patch<NavigationStateModel>({
        navigationItems: iif<NavigationPresenterModel>(
          () => !module,
          // It's a global item if there is no module:
          patch<NavigationPresenterModel>({
            systemViews: iif<NavigationItem[]>(
              (items) =>
                items.findIndex((item) => item.id === navItem.id) === -1,
              insertItem<NavigationItem>(navItem),
              updateItem((item) => item.id === navItem.id, navItem),
            ),
          }),
          // Otherwise, it's a module item:
          patch<NavigationPresenterModel>({
            moduleViews: iif(
              () => !moduleItem,
              insertItem({ ...module, views: [navItem] }),
              updateItem<DataModuleWithViewItems>(
                (mod) => mod.id === module.id,
                patch({
                  views: iif<NavigationItem[]>(
                    (items) =>
                      items.findIndex((item) => item.id === navItem.id) === -1,
                    insertItem<NavigationItem>(navItem),
                    updateItem((item) => item.id === navItem.id, navItem),
                  ),
                }),
              ),
            ),
          }),
        ),
      }),
    );
  }

  @Action(NavigationStateActions.UpdateNavigationItem)
  updateNavigationItem(
    { setState }: StateContext<NavigationStateModel>,
    { moduleId, navItem }: NavigationStateActions.UpdateNavigationItem,
  ) {
    setState(
      patch<NavigationStateModel>({
        navigationItems: iif<NavigationPresenterModel>(
          () => !moduleId,
          // It's a global item if there is no module:
          patch<NavigationPresenterModel>({
            systemViews: updateItem<NavigationItem>(
              (item) => item.id === navItem.id,
              navItem,
            ),
            moduleViews: updateItem<DataModuleWithViewItems>(
              (mod) => mod.id === navItem.id,
              patch(navItem),
            ),
          }),
          // Otherwise, it's a module item:
          patch({
            moduleViews: updateItem<DataModuleWithViewItems>(
              (mod) => mod.id === moduleId,
              patch({
                views: iif<NavigationItem[]>(
                  (items) =>
                    items.findIndex((item) => item.id === navItem.id) !== -1,
                  updateItem((item) => item.id === navItem.id, navItem),
                ),
              }),
            ),
          }),
        ),
      }),
    );
  }

  @Action(NavigationStateActions.RemoveNavigationItem)
  removeNavigationItem(
    { getState, setState }: StateContext<NavigationStateModel>,
    { navItemId, moduleId }: NavigationStateActions.RemoveNavigationItem,
  ) {
    const { navigationItems } = getState();

    const moduleItem = navigationItems.moduleViews.find(
      (mod) => mod.id === moduleId,
    );

    setState(
      patch<NavigationStateModel>({
        navigationItems: iif<NavigationPresenterModel>(
          () => !moduleItem,
          // If it's global nav item (or the whole module):
          patch<NavigationPresenterModel>({
            systemViews: removeItem((item) => item.id === navItemId),
            moduleViews: removeItem((item) => item.id === navItemId),
          }),
          // Otherwise, if it's module nav item:
          patch<NavigationPresenterModel>({
            moduleViews: iif<DataModuleWithViewItems[]>(
              () =>
                !moduleItem.views.filter((item) => item.id !== navItemId)
                  .length,
              // Remove the module from the menu if there are no items except the target one:
              removeItem<DataModuleWithViewItems>((mod) => mod.id === moduleId),
              // otherwise, remove the item from module:
              updateItem<DataModuleWithViewItems>(
                (mod) => mod.id === moduleId,
                patch({
                  views: removeItem<NavigationItem>(
                    (item) => item.id === navItemId,
                  ),
                }),
              ),
            ),
          }),
        ),
      }),
    );
  }
  @Action(NavigationStateActions.FetchItemsPinnedToSidebarV2)
  fetchItemsPinnedToSidebarV2({
    patchState,
  }: StateContext<NavigationStateModel>) {
    return this.sideBarService
      .getViewsForSidebarV2({
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
      })
      .pipe(
        tap((res) => {
          const navItems = this.navFactoryV2Service.createPresenterModel(res);

          patchState({
            navigationItemsV2: navItems,
            servicesNavigationConfig: {
              enabled:
                !!res.serviceViewsStatus?.hasServiceCatalog ||
                !!res.serviceViewsStatus?.hasRequests ||
                !!res.serviceViewsStatus?.hasInbox ||
                !!res.serviceViewsStatus.hasObserverRequests,
            },
            sidebarStatus: navItems.serviceViewsStatus,
          });
        }),
      );
  }
  @Action(NavigationStateActions.FetchSidebarStatusCounts)
  fetchSidebarStatusCounts({ patchState }: StateContext<NavigationStateModel>) {
    this.socketService
      .subscribeToModifiedRequestsCounts()
      .pipe(
        switchMap(() =>
          this.sideBarService
            .getViewsForSidebarV2({
              include: 2, // Only fetch counts
              context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
            })
            .pipe(
              tap((res) => {
                const serviceViewsStatus = {
                  onGoingInbox: res?.serviceViewsStatus?.onGoingInbox || 0,
                  onGoingRequests:
                    res?.serviceViewsStatus?.onGoingRequests || 0,
                  onGoingObserverRequests:
                    res?.serviceViewsStatus?.onGoingObserverRequests || 0,
                  hasInbox: res?.serviceViewsStatus?.hasInbox || false,
                  hasObserverRequests:
                    res?.serviceViewsStatus?.hasObserverRequests || false,
                  hasRequests: res?.serviceViewsStatus?.hasRequests || false,
                  hasServiceCatalog:
                    res?.serviceViewsStatus?.hasServiceCatalog || false,
                };
                patchState({
                  sidebarStatus: serviceViewsStatus,
                });
              }),
            ),
        ),
      )
      .subscribe();
  }
}
